import { useEffect, useState } from "react";
import { BsGraphDownArrow, BsGraphUpArrow } from "react-icons/bs";
import { GiBull } from "react-icons/gi";
import { ImParagraphRight } from "react-icons/im";
import { MdOutlineHorizontalRule } from "react-icons/md";
import { api } from "../../../services/api";

// Janela usada para calcular a média histórica de comparação.
const DIAS_MEDIA = 90;

// Margem (em %) acima/abaixo da média para considerar a venda
// favorável ou desfavorável. Dentro dessa margem é tratado como neutro.
const MARGEM_PERCENTUAL = 2;

const RecomenVenda = () => {
  const [precoAtual, setPrecoAtual] = useState(null);
  const [dataAtual, setDataAtual] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    buscarDados();
  }, []);

  const buscarDados = async () => {
    setCarregando(true);
    setErro(null);

    try {
      const [ultimoRes, historicoRes] = await Promise.all([
        api.get("/mercado/boi-gordo/"),
        api.get("/mercado/boi-gordo/historico/", {
          params: { dias: DIAS_MEDIA },
        }),
      ]);

      setPrecoAtual(Number(ultimoRes.data?.price) || null);
      setDataAtual(ultimoRes.data?.data || null);
      setHistorico(Array.isArray(historicoRes.data) ? historicoRes.data : []);
    } catch (error) {
      console.error("Erro ao carregar dados para recomendação de venda:", error);
      setErro("Não foi possível carregar as cotações para gerar a recomendação.");
      setPrecoAtual(null);
      setHistorico([]);
    } finally {
      setCarregando(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" });
  };

  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined || Number.isNaN(valor)) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const precos = historico.map((item) => Number(item.preco_arroba) || 0);
  const precoMedio =
    precos.length > 0 ? precos.reduce((soma, p) => soma + p, 0) / precos.length : null;

  const diferencaPercentual =
    precoAtual !== null && precoMedio ? ((precoAtual - precoMedio) / precoMedio) * 100 : null;

  // Comparação simples: preço bem acima da média histórica recente é
  // favorável para vender; bem abaixo é desfavorável; próximo da média é neutro.
  let recomendacao = "indisponivel";
  if (diferencaPercentual !== null) {
    if (diferencaPercentual > MARGEM_PERCENTUAL) recomendacao = "favoravel";
    else if (diferencaPercentual < -MARGEM_PERCENTUAL) recomendacao = "desfavoravel";
    else recomendacao = "neutro";
  }

  const configRecomendacao = {
    favoravel: {
      titulo: "Momento favorável para venda",
      descricao: `O preço atual está ${diferencaPercentual?.toFixed(1)}% acima da média dos últimos ${DIAS_MEDIA} dias.`,
      cor: "green",
      icone: <BsGraphUpArrow size={28} />,
    },
    desfavoravel: {
      titulo: "Momento desfavorável para venda",
      descricao: `O preço atual está ${Math.abs(diferencaPercentual || 0).toFixed(1)}% abaixo da média dos últimos ${DIAS_MEDIA} dias.`,
      cor: "red",
      icone: <BsGraphDownArrow size={28} />,
    },
    neutro: {
      titulo: "Preço estável em relação à média",
      descricao: `O preço atual está próximo da média dos últimos ${DIAS_MEDIA} dias (diferença de ${diferencaPercentual?.toFixed(1)}%).`,
      cor: "yellow",
      icone: <MdOutlineHorizontalRule size={28} />,
    },
    indisponivel: {
      titulo: "Sem dados suficientes",
      descricao: "Não há histórico de cotações suficiente para calcular uma recomendação.",
      cor: "gray",
      icone: <MdOutlineHorizontalRule size={28} />,
    },
  };

  const corPorTipo = {
    green: {
      bg: "bg-green-50",
      border: "border-green-500",
      texto: "text-green-700",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-500",
      texto: "text-red-700",
    },
    yellow: {
      bg: "bg-yellow-50",
      border: "border-yellow-500",
      texto: "text-yellow-700",
    },
    gray: {
      bg: "bg-gray-50",
      border: "border-gray-400",
      texto: "text-gray-600",
    },
  };

  const atual = configRecomendacao[recomendacao];
  const cores = corPorTipo[atual.cor];

  const ultimosRegistros = [...historico].reverse().slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ImParagraphRight size={38} />
            Recomendação de Venda
          </h2>
          <p className="text-gray-600 mt-2">
            Compara o preço atual da arroba do boi gordo com a média histórica recente
          </p>
        </div>

        {erro && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
            {erro}
          </div>
        )}

        {carregando ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando cotações...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-500">
                <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                  <GiBull size={20} /> Preço Atual
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {formatarMoeda(precoAtual)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Atualizado em: {formatarData(dataAtual)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Média ({DIAS_MEDIA} dias)
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {formatarMoeda(precoMedio)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500">
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Diferença vs. média
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {diferencaPercentual !== null ? `${diferencaPercentual.toFixed(1)}%` : "-"}
                </p>
              </div>
            </div>

            <div
              className={`rounded-xl shadow-sm p-6 mb-6 border-l-4 ${cores.bg} ${cores.border}`}
            >
              <div className="flex items-start gap-4">
                <div className={cores.texto}>{atual.icone}</div>
                <div>
                  <h3 className={`text-lg font-bold ${cores.texto}`}>{atual.titulo}</h3>
                  <p className="text-gray-700 mt-1">{atual.descricao}</p>
                  <p className="text-xs text-gray-500 mt-3">
                    Recomendação simples baseada na comparação entre o preço atual e a
                    média histórica de {DIAS_MEDIA} dias. Não considera custos de
                    produção, frete ou outros fatores — use como referência adicional,
                    não como decisão automática.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-800 mb-4">
                Últimas cotações consideradas
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-3 text-left text-sm font-semibold">DATA</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        PREÇO DA @ (R$)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ultimosRegistros.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{formatarData(item.data)}</td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {formatarMoeda(item.preco_arroba)}
                        </td>
                      </tr>
                    ))}
                    {ultimosRegistros.length === 0 && (
                      <tr>
                        <td colSpan="2" className="text-center py-8 text-gray-500">
                          Nenhum registro encontrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecomenVenda;
